'use client'

import * as React from 'react'
import type { TurnstileApi, TurnstileWidgetId } from './turnstile-script'
import { loadTurnstileScript } from './turnstile-script'
import {
  LOCAL_DEVELOPMENT_TURNSTILE_TOKEN as LOCAL_DEVELOPMENT_TURNSTILE_TOKEN_VALUE,
  getTurnstileSiteKey,
  shouldSendTurnstileTokenHeader as shouldSendTurnstileTokenHeaderValue,
} from './turnstile-site-key'

export const TURNSTILE_TOKEN_HEADER = 'X-Turnstile-Token'
export const LOCAL_DEVELOPMENT_TURNSTILE_TOKEN = LOCAL_DEVELOPMENT_TURNSTILE_TOKEN_VALUE

export function shouldSendTurnstileTokenHeader(token: string) {
  return shouldSendTurnstileTokenHeaderValue(token)
}

export interface TurnstileContextValue {
  execute(): Promise<string>
  isPending: boolean
}

interface PrepareTurnstileRequestHeadersOptions {
  enabled: boolean
  headers: Headers
  turnstile: Pick<TurnstileContextValue, 'execute'>
}

export const TurnstileContext = React.createContext<TurnstileContextValue>({
  execute: () => Promise.reject(new Error('Turnstile has not been initialized')),
  isPending: false,
})

export async function prepareTurnstileRequestHeaders({
  enabled,
  headers,
  turnstile,
}: PrepareTurnstileRequestHeadersOptions) {
  headers.delete(TURNSTILE_TOKEN_HEADER)

  if (!enabled) {
    return
  }

  const token = await turnstile.execute()

  if (!token || !shouldSendTurnstileTokenHeader(token)) {
    throw new Error('Turnstile did not return a token')
  }

  headers.set(TURNSTILE_TOKEN_HEADER, token)
}

function toError(error: unknown) {
  return error instanceof Error ? error : new Error(String(error))
}

export function TurnstileProvider({
  action = 'auth',
  children,
  enabled,
}: {
  action?: string
  children: React.ReactNode
  enabled: boolean
}) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const turnstileRef = React.useRef<TurnstileApi | null>(null)
  const widgetIdRef = React.useRef<TurnstileWidgetId | null>(null)
  const renderPromiseRef = React.useRef<Promise<TurnstileWidgetId> | null>(null)
  const renderActionRef = React.useRef<string | null>(null)
  const pendingRef = React.useRef<{
    reject(error: Error): void
    resolve(token: string): void
  } | null>(null)
  const [isPending, setIsPending] = React.useState(false)

  const rejectPending = React.useCallback((error: Error) => {
    const pending = pendingRef.current
    pendingRef.current = null
    setIsPending(false)
    pending?.reject(error)
  }, [])

  const resetRenderedWidget = React.useCallback(() => {
    if (widgetIdRef.current && turnstileRef.current) {
      turnstileRef.current.remove(widgetIdRef.current)
    }

    widgetIdRef.current = null
    renderPromiseRef.current = null
    renderActionRef.current = null
  }, [])

  const renderWidget = React.useCallback(() => {
    if (widgetIdRef.current) {
      if (renderActionRef.current === action) {
        return widgetIdRef.current
      }

      resetRenderedWidget()
      rejectPending(new Error('Turnstile challenge cancelled'))
    }

    if (renderPromiseRef.current) {
      if (renderActionRef.current === action) {
        return renderPromiseRef.current
      }

      renderPromiseRef.current = null
      renderActionRef.current = null
    }

    renderActionRef.current = action

    const renderPromise = loadTurnstileScript()
      .then(turnstile => {
        if (renderPromiseRef.current !== renderPromise || renderActionRef.current !== action) {
          throw new Error('Turnstile render superseded')
        }

        if (!containerRef.current) {
          throw new Error('Turnstile container is not mounted')
        }

        turnstileRef.current = turnstile
        // Widget mode is configured as Invisible in Cloudflare; do not pass visible appearance modes.
        const widgetId = turnstile.render(containerRef.current, {
          sitekey: getTurnstileSiteKey(),
          action,
          execution: 'execute',
          'response-field': false,
          callback: token => {
            const pending = pendingRef.current
            pendingRef.current = null
            setIsPending(false)
            pending?.resolve(token)
          },
          'error-callback': errorCode => {
            rejectPending(new Error(`Turnstile failed: ${errorCode}`))
          },
          'expired-callback': () => {
            rejectPending(new Error('Turnstile token expired'))
          },
          'timeout-callback': () => {
            rejectPending(new Error('Turnstile challenge timed out'))
          },
          'unsupported-callback': () => {
            rejectPending(new Error('Turnstile is not supported in this browser'))
          },
        })

        widgetIdRef.current = widgetId
        return widgetId
      })
      .catch(error => {
        if (renderPromiseRef.current === renderPromise) {
          renderPromiseRef.current = null
          renderActionRef.current = null
        }

        throw error
      })

    renderPromiseRef.current = renderPromise
    return renderPromiseRef.current
  }, [action, rejectPending, resetRenderedWidget])

  React.useEffect(() => {
    if (!enabled) {
      return
    }

    void Promise.resolve(renderWidget()).catch(() => {})
  }, [enabled, renderWidget])

  React.useEffect(() => {
    if (enabled) {
      return
    }

    resetRenderedWidget()
    turnstileRef.current = null
    rejectPending(new Error('Turnstile challenge cancelled'))
  }, [enabled, rejectPending, resetRenderedWidget])

  React.useEffect(() => {
    return () => {
      resetRenderedWidget()

      rejectPending(new Error('Turnstile challenge cancelled'))
    }
  }, [rejectPending, resetRenderedWidget])

  const execute = React.useCallback(async () => {
    const widgetId = await renderWidget()
    const turnstile = turnstileRef.current

    if (!turnstile) {
      throw new Error('Turnstile is not ready')
    }

    if (pendingRef.current) {
      rejectPending(new Error('Turnstile challenge superseded'))
    }

    turnstile.reset(widgetId)
    setIsPending(true)

    const { promise, reject, resolve } = Promise.withResolvers<string>()
    pendingRef.current = { resolve, reject }

    try {
      turnstile.execute(widgetId)
    } catch (error) {
      rejectPending(toError(error))
    }

    return promise
  }, [rejectPending, renderWidget])

  const value = React.useMemo(
    () => ({
      execute,
      isPending,
    }),
    [execute, isPending],
  )

  return (
    <TurnstileContext.Provider value={value}>
      {children}
      {enabled ? (
        <div
          ref={containerRef}
          aria-hidden="true"
          className="pointer-events-none absolute h-0 w-0 overflow-hidden"
        />
      ) : null}
    </TurnstileContext.Provider>
  )
}

export function useTurnstile() {
  return React.useContext(TurnstileContext)
}
